import React from 'react'

// TODO: maybe it makes more sense to just split the user state to have 3 child arrays, functionalities, roles, and applications. But that would require some refactoring.
export const userHasFunctionality = (user, functionality) => user && user.authorizations && user.authorizations.includes(functionality)
export const userHasRole = (user, role) => user && user.authorizations && user.authorizations.includes('ROLE_' + role)
export const userHasApplication = (user, application) => user && user.authorizations && user.authorizations.includes('APPLICATION_' + application)

// TODO: This should probably be moved to become a utility component since it returns JSX
export const getUserTypesFromAffiliations = (affiliations) => {
  if (!affiliations) { return undefined }
  return affiliations.map((affiliation, index) => {
    let name = `${affiliation.name.substring(0, 1)}${affiliation.name.substring(1).toLowerCase()}`
    if (affiliation.name === 'OTHER') {
      name = 'Other Requester'
    } else if (affiliation.name === 'SAR' && affiliation.lappSar && affiliation.sappSar && affiliation.underwriter) {
      name = 'LAPP SAR, SAPP SAR, Underwriter'
    } else if (affiliation.name === 'SAR' && affiliation.underwriter) {
      name = affiliation.lappSar ? 'LAPP SAR/Underwriter' : 'Underwriter'
    } else if (affiliation.name === 'SAR') {
      name = affiliation.lappSar ? (affiliation.sappSar ? 'LAPP SAR, SAPP SAR' : 'LAPP SAR') : 'SAPP SAR'
    }
    return index !== affiliations.length - 1 ? <span key={name}>{`${name}, `}</span> : <span key={name}>{`${name}`}</span>
  })
}

/**
 * A string represntation of the user's type from the line of business's perspective
 * 
 * @author Alex Clary
 * 
 * @param {*} user a user object
 * 
 * @returns {string} string represntation of the user's type from the line of business's perspective
 */
export const userTypeString = (user) => {
  if (!user || !user.authorizations) { return null; }
  if (userHasRole(user, 'CO')) { return 'LGY CO User'; }
  if (userHasRole(user, 'RLC Loan Processor')) { return 'RLC User'; }
  if (userHasRole(user, 'SAR')) { return 'SAR'; }
  if (userHasRole(user, 'Appraiser')) { return 'Appraiser'; }
  if (userHasRole(user, 'Servicer')) { return 'Servicer'; }
  if (userHasRole(user, 'Inspector')) { return 'Inspector'; }
  if (userHasRole(user, 'Other Requester')) { return 'Other Requester'; }
  if (userHasRole(user, 'Inspector')) { return 'Inspector'; }
  if (userHasRole(user, 'Lender')) { return 'Lender'; }
  if (userHasRole(user, 'Veteran')) { return 'Veteran'; }
  if (userHasRole(user, 'Veteran Service Organizations')) { return 'VSO'; }
  return null;
}

export const getUserAffiliation = (user) => user.isAffiliate ? 'VA Affiliate' : userHasRole(user, 'Veteran Service Organizations') ? 'VSO' : (userHasApplication(user, 'FFPS') ? 'VA Employee, FFPS' : 'VA Employee')

/**
 * Returns the account status from the code
 * 
 * @author Alex Clary
 * 
 * @param {string|number} accountStatusCode 
 * 
 * @returns {string} the account status associated with the provided code, or 'Unknown' otherwise
 */
export const accountStatusStringFromCode = (accountStatusCode) => accountStatuses[accountStatusCode] ? accountStatuses[accountStatusCode].name : 'Unknown'

/**
 * Represents the (currently non-existant) account status lookup table. This is an object rather than an array because
 * retreiving a value via code is the most common use case. This is not a simple key:value pair to allow expansion in 
 * the future without messy code changes.
 */
export const accountStatuses = {
  '0': {
    code: 0,
    name: 'Inactive'
  },
  '1': {
    code: 1,
    name: 'Active'
  },
  '2': {
    code: 2,
    name: 'Terminated'
  },
  '3': {
    code: 3,
    name: 'Batch Inactive'
  },
  '4': {
    code: 4,
    name: 'Registration Inactive'
  },
  '5': {
    code: 5,
    name: 'Admin Inactive / Removed'
  },
  '6': {
    code: 6,
    name: 'Pending Reactivation'
  }
}

// This will make sure names like Van Der Bilt, O'Donnell, and Smith-Johnson will have correct case
export const caseCorrect = (name) =>
  (name && name.length > 2) ?
    name.charAt(0).toUpperCase() + name.substring(1).replace(/[ '-][a-zA-Z]/g,
      function (x) {
        return x.charAt(0) + x.charAt(1).toUpperCase();
      })
    : name.charAt(0).toUpperCase() + name.substring(1)

export const replacePercentEscapedSpaces = (name) => (name && name.length > 2) ? name.replace(/%20/g, ' ') : name

/**
 * Formats a user's name information into a single string using only minimal name fields
 * 
 * @author Alex Clary
 * 
 * @param {*} user the user object (e.g. from redux)
 */
export const formatNameCompact = (user) =>
  caseCorrect(user.firstName ? user.firstName : '') + ' ' + caseCorrect(user.lastName ? user.lastName : '')

/**
 * Formats a user's name information into a single string using all available name fields
 * 
 * @author Alex Clary
 * 
 * @param {*} user the user object (e.g. from redux)
 */
export const formatNameFull = (user) =>
  caseCorrect(user.firstName ? user.firstName : '') + ' ' + caseCorrect(user.middleName && user.middleName !== 'NOT_FOUND' ? user.middleName + ' ' : '')
  + caseCorrect(user.lastName ? user.lastName : '') + caseCorrect(user.suffixName ? ' ' + user.suffixName : '')

/**
 * Formats a user's phone number information into a single string, including their secondary phone information, extensionss, etc.
 * 
 * @author Alex Clary
 * 
 * @param {*} user the user object (e.g. from redux)
 */
export const phoneNumberStringFromUser = (user) => {
  if (!user) {
    return null
  }
  const primaryPhoneString = user.primaryPhoneNumber ? (user.primaryPhoneNumberType + ': ' + user.primaryPhoneNumber + (user.primaryPhoneNumberExtension ? ('x' + user.primaryPhoneNumberExtension) : '')) : ''
  const secondaryPhoneString = user.secondaryPhoneNumber ? (', ' + user.secondaryPhoneNumberType + ': ' + user.secondaryPhoneNumber + (user.secondaryPhoneNumberExtension ? ('x' + user.secondaryPhoneNumberExtension) : '')) : ''
  return primaryPhoneString + secondaryPhoneString
}